import classNames from 'classnames';
import { memo, type ReactElement, type ReactNode, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import { Tooltip } from '@air/primitive-tooltip';
import { formatBytes } from '@air/utilities';
import { isPdfRenderedExtension } from '@air/utils-clips';

import { type SelectionMouseModifiers } from '~/hooks/useSelectionCommit';

export type SelectAssetModalAssetItemDragSourceParams = {
  clip: Clip;
  children: ReactNode;
};

export type SelectAssetModalAssetItemRenderDragSource = (
  params: SelectAssetModalAssetItemDragSourceParams,
) => ReactElement;

export type SelectAssetModalAssetItemToggleOptions = {
  modifiers: SelectionMouseModifiers;
};

export type SelectAssetModalToggleOptions = SelectAssetModalAssetItemToggleOptions & {
  orderedClips: Clip[];
};

export type SelectAssetModalAssetInfoProps = {
  clip: Clip;
  className?: string;
  compact?: boolean;
};

export const SelectAssetModalAssetInfo = memo(({ clip, className, compact }: SelectAssetModalAssetInfoProps) => {
  const clipTitle = clip.title ?? clip.importedName;

  const meta = useMemo(() => {
    const meta = [];

    if (clip.ext) {
      meta.push(clip.ext.toUpperCase());
    }

    if (clip.size) {
      meta.push(formatBytes(clip.size));
    }

    if (!isPdfRenderedExtension(clip.ext) && clip.width && clip.height) {
      meta.push(`${clip.width} x ${clip.height}`);
    }

    return meta.join(' · ');
  }, [clip.ext, clip.height, clip.size, clip.width]);

  return (
    <div className={classNames('flex flex-col gap-0.5 p-2', className)}>
      <Tooltip label={clipTitle} side="top" sideOffset={4}>
        <p className={classNames('truncate font-medium', compact ? 'text-14' : 'text-16')}>{clipTitle}</p>
      </Tooltip>
      <p className="truncate ads:text-metadata text-12 text-gray-9">{meta}</p>
    </div>
  );
});

SelectAssetModalAssetInfo.displayName = 'SelectAssetModalAssetInfo';
